import React, { useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
} from '@mui/material';
import { Print as PrintIcon, Download as DownloadIcon } from '@mui/icons-material';
import QRCode from 'react-qr-code';
import BarcodeDisplay from './BarcodeDisplay';

interface QRPrintModalProps {
  open: boolean;
  onClose: () => void;
  qrValue: string;
  moldNumber: string;
  moldName: string;
}

export const QRPrintModal: React.FC<QRPrintModalProps> = ({
  open,
  onClose,
  qrValue,
  moldNumber,
  moldName,
}) => {
  const qrRef = useRef<HTMLDivElement>(null);

  const handlePrint = () => {
    // 새 창에서 인쇄
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (!printWindow) return;

    // 모든 SVG 가져오기 (첫 번째: QR, 두 번째: 바코드)
    const svgs = qrRef.current?.querySelectorAll('svg');
    const qrSvgString = svgs?.[0] ? new XMLSerializer().serializeToString(svgs[0]) : '';
    const barcodeSvgString = svgs?.[1] ? new XMLSerializer().serializeToString(svgs[1]) : '';

    // HTML 생성
    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>QR 코드 인쇄 - ${moldNumber}</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            @page {
              size: A4;
              margin: 15mm;
            }
            body {
              font-family: 'Pretendard Variable', 'Pretendard', sans-serif;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              padding: 40px 20px;
              min-height: 100vh;
            }
            .qr-container {
              text-align: center;
              max-width: 400px;
              width: 100%;
            }
            .qr-code {
              width: 300px;
              height: 300px;
              margin: 0 auto 20px;
            }
            .info {
              margin-bottom: 30px;
            }
            h1 {
              font-size: 24px;
              font-weight: bold;
              margin-bottom: 8px;
              color: #000;
            }
            p {
              font-size: 14px;
              color: #666;
            }
            .barcode {
              width: 100%;
              margin-top: 20px;
            }
            @media print {
              body {
                padding: 20px;
              }
            }
          </style>
        </head>
        <body>
          <div class="qr-container">
            <div class="qr-code">${qrSvgString}</div>
            <div class="info">
              <h1>${moldNumber}</h1>
              <p>${moldName}</p>
            </div>
            <div class="barcode">${barcodeSvgString}</div>
          </div>
          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
                // 인쇄 대화상자 닫힌 후 창 닫기
                setTimeout(function() {
                  window.close();
                }, 100);
              }, 500);
            };
          </script>
        </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
  };

  const handleDownload = () => {
    const svg = qrRef.current?.querySelector('svg');
    if (!svg) return;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const img = new Image();
    
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      
      canvas.toBlob((blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `QR_${moldNumber}.png`;
        link.click();
        URL.revokeObjectURL(url);
      });
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      className="qr-print-modal"
    >
      <DialogTitle>QR 코드 출력</DialogTitle>
      <DialogContent>
        <Box
          ref={qrRef}
          className="qr-print-content"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            py: 3,
          }}
        >
          <QRCode
            value={qrValue}
            size={300}
            style={{ height: 'auto', maxWidth: '100%', width: '100%' }}
          />
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" fontWeight="bold">
              {moldNumber}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {moldName}
            </Typography>
          </Box>
          <Box sx={{ width: '100%', maxWidth: 400 }}>
            <BarcodeDisplay
              value={moldNumber}
              showLabel={false}
              width={{ xs: 1.5, sm: 2, md: 2 }}
              height={{ xs: 50, sm: 60, md: 60 }}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={onClose} sx={{ color: 'white' }}>닫기</Button>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
        >
          저장
        </Button>
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
        >
          인쇄
        </Button>
      </DialogActions>
    </Dialog>
  );
};